import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import { BackupOutlined } from "@material-ui/icons";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import moment from "moment";
import {
  TextInput,
  DatePicker,
  PrimaryButton,
  SecondaryButton,
  FormLabel,
  ModalError,
  ModalSuccess,
  AttachmentFilesCard,
} from "components";
import { hardBaseUrl } from "services/urlConstant";
import {
  getErrors,
  formatPayloadTimezone,
  stringTruncate,
  formatFileSize,
} from "utils";

const useStyles = makeStyles(() => ({
  dropzone: {
    border: "2px dashed #ccc",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "transparent",
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
  },
  dropzoneActive: {
    backgroundColor: "#f0f0f0",
    borderColor: "#2196f3",
  },
  uploadIcon: {
    fontSize: 40,
    color: "#999",
  },
}));

const UpdateModal = ({ open, onClose, onSuccess }) => {
  const localClasses = useStyles();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({
    version: "",
    release_date: moment(),
    description: "",
  });
  const [file, setFile] = useState(null);

  const onDrop = useCallback(acceptedFiles => {
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".apk",
    multiple: false,
  });

  const handleChange = (value, key) => {
    setPayload(prev => ({ ...prev, [key]: value }));
  };

  const handleUpdate = async () => {
    if (
      !payload.version ||
      !payload.release_date ||
      !payload.description ||
      !file
    ) {
      ModalError("Please fill in all required fields and upload an APK file.");
      return;
    }

    setLoading(true);
    try {
      // Step 1: Initiate Upload
      const initRes = await axios.post(
        `${hardBaseUrl}/storage/upload/init`,
        { filenames: [file.name] },
        { headers }
      );

      const { url, filename: tempFilename } = initRes.data.data[0];

      if (!url) {
        throw new Error("Failed to get upload URL from server.");
      }

      // Step 2: Upload to S3 (Using a clean axios instance to avoid global auth headers)
      const s3Axios = axios.create();
      await s3Axios.put(url, file, {
        headers: {
          "Content-Type":
            file.type || "application/vnd.android.package-archive",
        },
      });

      // Step 3: Update App Metadata
      await axios.post(
        `${hardBaseUrl}/admin/app/update`,
        {
          version: payload.version,
          release_date: formatPayloadTimezone(payload.release_date),
          description: payload.description,
          file_name: tempFilename,
          original_filename: file.name,
        },
        { headers }
      );

      ModalSuccess("App version updated successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      const errorMessage = getErrors(error.response);
      if (errorMessage === "An unknown error occurred") {
        ModalError(
          error.message ||
            "An unexpected error occurred during the update process."
        );
      } else {
        ModalError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={!loading && onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update APP</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Upload a new APK version to replace the current application
        </Typography>

        <Grid container direction="column" spacing={2} mt={1}>
          <Grid item>
            <FormLabel label="Version" required />
            <TextInput
              placeholder="e.g. 1.0.1"
              value={payload.version}
              onChange={e => handleChange(e.target.value, "version")}
              disabled={loading}
            />
          </Grid>

          <Grid item>
            <FormLabel label="Release Date" required />
            <DatePicker
              value={payload.release_date}
              onChange={date => handleChange(date, "release_date")}
              disabled={loading}
            />
          </Grid>

          <Grid item>
            <FormLabel label="Description" required />
            <TextInput
              placeholder="Enter release notes..."
              value={payload.description}
              onChange={e => handleChange(e.target.value, "description")}
              multiline
              rows={4}
              disabled={loading}
            />
          </Grid>

          <Grid item>
            <FormLabel label="Upload file" required />
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              mb={1}
            >
              Only .apk files are allowed. Max size 100MB
            </Typography>

            <div
              {...getRootProps({
                className: `${localClasses.dropzone} ${
                  isDragActive ? localClasses.dropzoneActive : ""
                }`,
              })}
            >
              <input {...getInputProps()} />
              <BackupOutlined className={localClasses.uploadIcon} />
              <Typography variant="body2">
                {file ? file.name : "Drag files or click to upload"}
              </Typography>
            </div>

            {file && (
              <Box mt={2}>
                <AttachmentFilesCard
                  fileName={stringTruncate(file.name, 30)}
                  fileSize={formatFileSize(file.size)}
                  handleRemove={() => setFile(null)}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <SecondaryButton label="Cancel" onClick={onClose} disabled={loading} />
        <PrimaryButton
          label={loading ? "Updating..." : "Update"}
          onClick={handleUpdate}
          loading={loading}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default UpdateModal;
